import React from 'react';
import {Box} from '@mui/material';
import {Header} from 'app/components/FleetManager/Header';
import {IndeterminateProgressBar} from 'app/components/sharedReactComponents/IndeterminateProgressBar';
import {UnifyManager} from 'app/components/UnifyProjects/UnifyManager/UnifyManager';
import {useProjects} from 'app/components/UnifyProjects/hooks/useProjects';
import {useUnifyStreamingDestination} from 'app/components/UnifyProjects/hooks/useUnifyStreamingDestination';
import {noop} from 'app/util/noop';
import {UnifyWelcome} from 'app/components/UnifyProjects/UnifyWelcome/UnifyWelcome';
import {useTeamBilling} from 'app/components/BillingManager/hooks/useTeamBilling';
import {useActualEvents} from 'app/components/sharedReactComponents/Events/hooks/useActualEvents';
import {useCloud} from 'app/components/features/cloud';
import {StyleSx, Sx} from 'app/types/common';
import {FeatureLockNotice} from 'app/components/entities/billing/base';
import {packSx} from 'app/util/packSx/packSx';

export function UnifyProjects() {
  const {team, user} = useCloud();
  const {actions} = useTeamBilling();

  const teamId = team.id;
  const {caps} = team;

  const {role} = user;

  const hasSchedule = caps.cms.enabled;

  const {loading, projects, refetchProjects, getProject} = useProjects({
    teamId,
    enabled: caps.unify.enabled,
  });

  const actualEvents = useActualEvents({teamId, enabled: hasSchedule});

  const {streamingDestinations, onChangeStreamingDestination} =
    useUnifyStreamingDestination(refetchProjects);

  if (!caps.unify.enabled) {
    return (
      <div className="container">
        {caps.unify.reason === 'subscription_is_dq' ? (
          <LockNotice sx={{my: 4}} paymentAccess={role.canEditBilling()} />
        ) : (
          <UnifyWelcome
            sx={{mt: 4}}
            canManage={role.canEditBilling()}
            onManage={() => actions.manageUnify()}
          />
        )}
      </div>
    );
  }

  return (
    <Box>
      <Header showSearch={false} tools={{pair: false}} searchValue="" onSearch={noop} />

      {loading ? (
        <IndeterminateProgressBar />
      ) : (
        <UnifyManager
          teamId={teamId}
          role={role}
          projects={projects}
          actualEvents={actualEvents}
          streamingDestinations={streamingDestinations}
          teamCapabilities={caps}
          onReload={refetchProjects}
          getById={getProject}
          onChangeStreamingDestination={onChangeStreamingDestination}
        />
      )}
    </Box>
  );
}

const bgSx: StyleSx = {
  backgroundImage:
    'image-set(url(/assets/img/background/unify/bg.png) 1x, url(/assets/img/background/unify/bg@2x.png) 2x), linear-gradient(250deg, #ADB5BD 8%, #FFFFFF 110%)',
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 0px, center',
  backgroundSize: 'contain',
};

interface LockProps extends Sx {
  paymentAccess: boolean;
}

function LockNotice({sx, paymentAccess}: LockProps) {
  return (
    <FeatureLockNotice
      sx={packSx(bgSx, sx)}
      logo={
        <Box
          width={170}
          component="img"
          src="/assets/img/logo/unify-logo-black.png"
          srcSet="/assets/img/logo/unify-logo-black@2x.png 2x"
          alt="Epiphan Unify logo"
        />
      }
      title="Epiphan Unify disabled"
      description="The Epiphan Unify page is currently disabled due to an unsuccessful payment. Please update your payment method to restore access."
      paymentAccess={paymentAccess}
    />
  );
}
